import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {Button, Radio, Space} from 'antd';
import {CloseOutlined, SearchOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {UnmountClosed} from 'react-collapse';
import {
  StyledBudgetSelector,
  StyledBudgetSelectorItem,
  StyledBudgetSelectorItemTitle,
  StyledBudgetSelectorItemDate,
  StyledBudgetSelectorItemDateTag,
  StyledBudgetSelectorFilterSpace,
  StyledBudgetSelectorResults
} from './StyledBudgetSelector';
import {StyledTransactionMatchingPanelRadioGroup} from '../TransactionMatchingPanel/StyledTransactionMatchingPanel';
import Spin from '../../../Spin';
import TransactionMatchingPanelFilters from '../TransactionMatchingPanel/TransactionMatchingPanelFilters';
import {budgetProcurementTypeConstants, budgetStatusConstants, invoiceTypeConstants} from '../../../../constants';
import {amountHelpers, budgetHelpers, dateHelpers, invoicesHelpers} from '../../../../utils/helpers';
import {budgetActions} from '../../../../state/actions/budget.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';

const defaultFilter = { amountType: '<' };

const activeBudgetStatuses = [budgetStatusConstants.ACTIVE, budgetStatusConstants.OVER_BUDGET];

const BudgetSelector = ({
  invoice,
  onSave,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [value, setValue] = useState(null);
  const [data, setData] = useState({
    results: [],
    loading: true,
    pagination: null,
  });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [filter, setFilter] = useState(defaultFilter);

  const isEmptyList = data.results.length === 0;

  const dispatch = useDispatch();

  const finishLoading = () => setData(prev => ({ ...prev, loading: false }));

  useEffect(() => {
    loadRecommendedBudgets(invoice);
  }, [invoice?.id, invoice?.vendor_local_id, invoice?.type]); // eslint-disable-line react-hooks/exhaustive-deps

  const storeBudgetsList = ({initial = true, response }) => {
    setData(prev => ({
      ...prev,
      loading: false,
      results: initial ? response.results : [...prev.results, ...response.results],
      pagination: response.pagination,
    }));
  }

  const loadBudgetItems = () => {
    loadBudgetsList({
      initial: true,
      query: { procurement_type: budgetProcurementTypeConstants.BUDGET_ITEM }
    });
  }

  const loadEmptyRecommendedBudgets = (invoice) => {
    const isInvoiceDocumentType = invoice?.type === invoiceTypeConstants.INVOICE;
    if (isInvoiceDocumentType) {
      // Load budgets for vendor and purchase order procurement type
      loadBudgetsList({
        query: {
          vendor_id: isInvoiceDocumentType ? invoice?.vendor_local_id : undefined,
          procurement_type: budgetProcurementTypeConstants.PURCHASE_ORDER
        },
        initial: true,
        successCallback: (response) => {
          // If no budgets found for vendor and purchase order, load procurement requests
          if (response.results.length === 0) {
            loadBudgetItems();
            return
          }
          storeBudgetsList({initial: true, response});
        }
      });
    } else {
      loadBudgetItems();
    }
  }

  const loadBatchBudgetRequests = (recommendedIds = []) => {
    if (recommendedIds.length === 0) return;

    dispatch(budgetActions.getBatchBudgetRequests({
      data: {budget_ids: recommendedIds, status: activeBudgetStatuses},
      successCallback: (response) => {
        // sort budgets in the same order as recommended ids
        const results = response.results || [];
        const sortedBudgets = recommendedIds.map(id => results.find(budget => budget.id === id)).filter(budget => !!budget);
        storeBudgetsList({initial: true, response: { ...response, results: sortedBudgets }});
      },
      errorCallback: finishLoading
    }));
  }

  const loadRecommendedBudgets = (invoice) => {
    const invoiceId = invoice?.id;
    if (!invoiceId) return;

    setData({ ...data, loading: true });

    const callbackFunc = (recommended = []) => {
      // If no recommended budgets, load all budgets
      if (recommended.length === 0) {
        loadEmptyRecommendedBudgets(invoice);
        return;
      }

      // Load recommended budgets details
      const recommendedIds = recommended?.map(budget => budget.id) || [];
      loadBatchBudgetRequests(recommendedIds);
    }

    dispatch(invoicesActions.getRecommendedBudgetRequests({
      id: invoiceId,
      successCallback: callbackFunc,
      errorCallback: () => callbackFunc()
    }));
  }

  const loadBudgetsList = ({query, initial = true, successCallback} = {}) => {
    setData({ ...data, loading: true });

    // Load all budgets
    dispatch(budgetActions.getBudgetRequests({
      query: { status: activeBudgetStatuses, ...query },
      successCallback: (response) => {
        if (successCallback) {
          successCallback?.(response);
          return;
        }
        storeBudgetsList({initial, response});
      },
      errorCallback: finishLoading
    }))
  }

  useEffect(() => {
    setFilter(defaultFilter);
  }, [invoice?.id]);

  const handleOnChange = (e) => {
    const value = e.target.value;
    setValue(value);
    onSave?.(value);
  }

  const handleSearchClick = () => setIsSearchOpen(!isSearchOpen);

  const handleOnFilter = (query) => {
    setFilter(query);
    loadBudgetsList({
      query: invoicesHelpers.convertMatchFilterPropsToApi(query, {
        dateGteKey: 'start_date', dateLteKey: 'end_date', amountLteKey: 'limit_amount_lte', amountGteKey: 'limit_amount_gte',
      }),
      initial: true,
    });
  }

  return (
    <StyledBudgetSelector {...rest}>
      <div className='justify-space-between d-flex align-items-center'>
        {t('Budget')}
        <Space size='small' style={{width: 'unset'}}>
          <Button
            icon={isSearchOpen ? <CloseOutlined /> : <SearchOutlined />}
            onClick={handleSearchClick}
          />
        </Space>
      </div>

      <UnmountClosed isOpened={isSearchOpen}>
        <StyledBudgetSelectorFilterSpace>
          <TransactionMatchingPanelFilters
            className='budget-selector-filters'
            defaultValues={filter}
            onFilter={handleOnFilter}
          />
        </StyledBudgetSelectorFilterSpace>
      </UnmountClosed>

      <Spin size='small' spinning={data.loading}>
        <StyledBudgetSelectorResults>
          {!isEmptyList && (
            <StyledTransactionMatchingPanelRadioGroup
              onChange={handleOnChange}
              value={value}
            >
              {data.results.map((budget, index) => (
                <Radio value={budget.id} key={index}>
                  <StyledBudgetSelectorItem size={2} direction='vertical'>
                    <StyledBudgetSelectorItemTitle>
                      <span>{budgetHelpers.getBudgetName(budget)}</span>
                      <span className='amount'>{amountHelpers.getAmountWithCurrencyCode(budget?.limit_amount || 0)}</span>
                    </StyledBudgetSelectorItemTitle>
                    <StyledBudgetSelectorItemDate>
                      Created on {dateHelpers.getDate(budget.created_at, 'DD MMM YYYY')}
                    </StyledBudgetSelectorItemDate>
                    <StyledBudgetSelectorItemDateTag>
                      {budget?.budget_name}
                    </StyledBudgetSelectorItemDateTag>
                  </StyledBudgetSelectorItem>
                </Radio>
              ))}
            </StyledTransactionMatchingPanelRadioGroup>
          )}
          {(isEmptyList && !data.loading) && (
            <div className='flex-center-center'>{t('emptyList')}</div>
          )}
        </StyledBudgetSelectorResults>
      </Spin>

    </StyledBudgetSelector>
  );
};

BudgetSelector.propTypes = {
  budgets: PropTypes.arrayOf(
    PropTypes.shape({
      budget_name: PropTypes.string,
      created_at: PropTypes.string,
      limit_amount: PropTypes.number,
      id: PropTypes.string,
      name: PropTypes.string,
    })
  )
};

export default BudgetSelector;
